import { Button, ButtonVariant, IconButton } from '@creditornot/cb-button';
import { MoreHorizontal } from '@creditornot/cb-icons';
import { MenuActionItem, MenuItemGroup } from '@creditornot/cb-menu';
import { LegacyPopover } from '@creditornot/cb-popover';
import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { CampaignType } from 'apis/restaurant-api/merchant-admin/campaigns/types';
import { Typography } from 'common/typography';

import ConfirmDeleteModal from './modals/ConfirmDeleteModal';
import ConfirmTerminateModal from './modals/ConfirmTerminateModal';
import { MOBILE_BREAKPOINT } from '../../../common/hooks/useIsMobile';
import { useCampaignModuleContext } from '../context';
import { useCampaignHandlers } from '../hooks/useCampaignHandlers';
import { CampaignAction } from '../types';

type CampaignActionsRowProps = {
  campaignId: string;
  campaignType: CampaignType;
  allowedActions: CampaignAction[];
};

type ActionItem = { onClick: () => void; text: string; variant: ButtonVariant };

const StyledPopover = styled(LegacyPopover)`
  z-index: 10;
`;

const ActionsPopoverContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 4px;
  gap: 8px;
  width: 172px;
`;

const CampaignButtons = styled.div`
  display: flex;
  flex-direction: row-reverse;
  align-items: flex-end;
  gap: 8px;

  @media (width <= ${MOBILE_BREAKPOINT}px) {
    flex-direction: row;
    justify-content: flex-end;
    & > :first-child {
      flex-grow: 1;
    }
  }
`;

export const CampaignActionsRow = ({ campaignId, campaignType, allowedActions }: CampaignActionsRowProps) => {
  const { merchantId, venues } = useCampaignModuleContext();
  const venueIds = venues.map(venue => venue.id);

  const {
    handleCloneCampaign,
    handleDeleteCampaign,
    handleEditCampaign,
    handleRestartCampaign,
    handleStopCampaign,
    handleTerminateCampaign,
  } = useCampaignHandlers({ merchantId, venueIds });

  const [isConfirmTerminateModalOpen, setIsConfirmTerminateModalOpen] = useState<boolean>(false);
  const [isConfirmDeleteModalOpen, setIsConfirmDeleteModalOpen] = useState<boolean>(false);
  const { formatMessage } = useIntl();

  const actionsConfig: Partial<Record<CampaignAction, ActionItem>> = {
    edit: {
      onClick: () => handleEditCampaign(campaignId, campaignType),
      text: formatMessage({ id: 'campaigns.edit-campaign' }),
      variant: 'primary',
    },
    terminate: {
      onClick: () => setIsConfirmTerminateModalOpen(true),
      text: formatMessage({ id: 'campaigns.end' }),
      variant: 'secondaryNegative',
    },
    clone: {
      onClick: () => handleCloneCampaign(campaignId),
      text: formatMessage({ id: 'campaigns.clone' }),
      variant: 'secondary',
    },
    stop: {
      onClick: () => handleStopCampaign(campaignId),
      text: formatMessage({ id: 'campaigns.stop' }),
      variant: 'secondaryNegative',
    },
    restart: {
      onClick: () => handleRestartCampaign(campaignId),
      text: formatMessage({ id: 'campaigns.restart' }),
      variant: 'primary',
    },
    delete: {
      onClick: () => setIsConfirmDeleteModalOpen(true),
      text: formatMessage({ id: 'campaigns.delete' }),
      variant: 'secondaryNegative',
    },
  };

  const actions: ActionItem[] = Object.entries(actionsConfig)
    .filter(([action]) => allowedActions.includes(action as CampaignAction))
    .map(([, action]) => action);

  const visibleActions = actions.slice(0, 2);
  const moreActions = actions.slice(2);

  return (
    <div onClick={evt => evt.stopPropagation()}>
      <ConfirmTerminateModal
        isOpen={isConfirmTerminateModalOpen}
        onTerminate={() => {
          handleTerminateCampaign(campaignId);
          setIsConfirmTerminateModalOpen(false);
        }}
        onBack={() => {
          setIsConfirmTerminateModalOpen(false);
        }}
      />
      <ConfirmDeleteModal
        isOpen={isConfirmDeleteModalOpen}
        onDelete={() => {
          handleDeleteCampaign(campaignId);
          setIsConfirmDeleteModalOpen(false);
        }}
        onBack={() => {
          setIsConfirmDeleteModalOpen(false);
        }}
      />
      <CampaignButtons>
        {visibleActions.map(action => (
          <Button size="small" key={action.text} variant={action.variant} onClick={action.onClick}>
            {action.text}
          </Button>
        ))}
        {moreActions.length > 0 && (
          <StyledPopover
            placement="bottom-end"
            showOnClick
            showOnHover={false}
            renderArrow={false}
            content={() => (
              <ActionsPopoverContainer>
                <MenuItemGroup>
                  {moreActions.map(action => (
                    <MenuActionItem
                      key={action.text}
                      onClick={action.onClick}
                      variant={action.variant === 'secondaryNegative' ? 'red' : 'black'}
                    >
                      <Typography.Body3>{action.text}</Typography.Body3>
                    </MenuActionItem>
                  ))}
                </MenuItemGroup>
              </ActionsPopoverContainer>
            )}
          >
            <IconButton variant="neutral" size="small">
              <MoreHorizontal />
            </IconButton>
          </StyledPopover>
        )}
      </CampaignButtons>
    </div>
  );
};
